import React, { createContext, useContext, useEffect, useState, useMemo, useCallback } from 'react';
import type { ThemePreference, ResolvedTheme } from '../types';

export type { ThemePreference, ResolvedTheme };

export interface ThemeContextValue {
  readonly preference: ThemePreference;
  readonly resolvedTheme: ResolvedTheme;
  readonly setPreference: (pref: ThemePreference) => void;
  readonly toggleTheme: () => void;
}

export const THEME_STORAGE_KEY = 'theme_preference';

export const ThemeContext = createContext<ThemeContextValue | undefined>(undefined);

export function getSystemTheme(): ResolvedTheme {
  if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') {
    return 'dark';
  }
  return window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light';
}

export function getStoredPreference(): ThemePreference {
  try {
    const val = localStorage.getItem(THEME_STORAGE_KEY);
    if (val === 'light' || val === 'dark' || val === 'system') {
      return val;
    }
  } catch {
    /* Ignore storage access errors (e.g. strict sandbox / quota) */
  }
  return 'system';
}

export interface ThemeProviderProps {
  readonly children: React.ReactNode;
}

export function ThemeProvider({ children }: ThemeProviderProps): React.JSX.Element {
  const [preference, setPreferenceState] = useState<ThemePreference>(getStoredPreference);
  const [systemTheme, setSystemTheme] = useState<ResolvedTheme>(getSystemTheme);

  const resolvedTheme: ResolvedTheme = useMemo(() => {
    return preference === 'system' ? systemTheme : preference;
  }, [preference, systemTheme]);

  const applyTheme = useCallback((theme: ResolvedTheme): void => {
    /* v8 ignore next */
    if (typeof document === 'undefined') return;

    const root = document.documentElement;
    root.classList.add('disable-transitions');
    root.setAttribute('data-theme', theme);
    root.style.colorScheme = theme;

    const meta = document.querySelector('meta[name="theme-color"]');
    if (meta) {
      meta.setAttribute('content', theme === 'dark' ? '#090F19' : '#F8FAFC');
    }

    if (typeof requestAnimationFrame === 'function') {
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          root.classList.remove('disable-transitions');
        });
      });
    } else {
      root.classList.remove('disable-transitions');
    }
  }, []);

  const setPreference = useCallback((newPref: ThemePreference): void => {
    setPreferenceState(newPref);
    try {
      localStorage.setItem(THEME_STORAGE_KEY, newPref);
    } catch {
      /* Ignore storage write failures */
    }
  }, []);

  const toggleTheme = useCallback((): void => {
    setPreference(resolvedTheme === 'dark' ? 'light' : 'dark');
  }, [resolvedTheme, setPreference]);

  useEffect(() => {
    applyTheme(resolvedTheme);
  }, [resolvedTheme, applyTheme]);

  // Listen to OS scheme changes
  useEffect(() => {
    /* v8 ignore next */
    if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') return;

    const mql = window.matchMedia('(prefers-color-scheme: dark)');
    const handler = (e: MediaQueryListEvent): void => {
      setSystemTheme(e.matches ? 'dark' : 'light');
    };

    mql.addEventListener('change', handler);
    return () => {
      mql.removeEventListener('change', handler);
    };
  }, []);

  // Multi-tab sync across browser storage events
  useEffect(() => {
    /* v8 ignore next */
    if (typeof window === 'undefined') return;

    const onStorage = (e: StorageEvent): void => {
      if (e.key === THEME_STORAGE_KEY) {
        const val = e.newValue as ThemePreference | null;
        setPreferenceState(val === 'light' || val === 'dark' || val === 'system' ? val : 'system');
      }
    };

    window.addEventListener('storage', onStorage);
    return () => {
      window.removeEventListener('storage', onStorage);
    };
  }, []);

  const value = useMemo(
    (): ThemeContextValue => ({
      preference,
      resolvedTheme,
      setPreference,
      toggleTheme,
    }),
    [preference, resolvedTheme, setPreference, toggleTheme]
  );

  return <ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>;
}

export function useTheme(): ThemeContextValue {
  const ctx = useContext(ThemeContext);
  if (!ctx) {
    throw new Error('useTheme must be used within a ThemeProvider');
  }
  return ctx;
}
